import { appState } from '../appState';
import { RouteLike } from './navigation';
import css from './navigation.module.less';
import { useAppSelector } from '@/redux';
import { t } from '@/translations/translator';
import { faQuestion } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Box, Menu, MenuItem } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import BrandImage from '@whiz-cart/ui-shared/brand/brandImage.js';
import { useStableMemo, useStableValue } from '@whiz-cart/ui-shared/hooks/useStable';
import { isLinkActive, NavLink } from '@whiz-cart/ui-shared/routing/navLink.component.tsx';
import c from 'classnames';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type NavigationGroupProps = {
    routes: RouteLike[];
    groupName: string;
    hideNavigation: () => void;
    disableCollapse: boolean;
    highlightItem?: (item: RouteLike) => boolean | undefined;
};

const subTreeMarkerSx = {
    '--size': '0.625em',
    ml: 'var(--size)',
    mb: 'var(--size)',
    width: 'var(--size)',
    height: 'var(--size)',
    border: 'solid currentcolor',
    borderWidth: '0 0 2px 2px',
};

const findActiveRoute = (routes: RouteLike[], path: string): RouteLike | undefined => {
    const active = routes.find((route) => {
        return isLinkActive({ pathname: path, to: route.link });
    });

    const activeChild = active?.children && findActiveRoute(active.children, path);

    return activeChild ?? active;
};

const InnerNavigationGroup = memo(function InnerNavigationGroup({
    routes,
    groupName,
    hideNavigation,
    highlightItem,
    activeRoute,
    noCollapse,
    show,
}: Pick<NavigationGroupProps, 'routes' | 'groupName' | 'hideNavigation' | 'highlightItem'> & {
    activeRoute?: RouteLike;
    noCollapse: boolean;
    show?: boolean;
}) {
    const handleButtonClick = useCallback(
        async (e: React.MouseEvent<HTMLDivElement>) => {
            if (noCollapse) return;
            const newValue = !show;
            appState.update((state) => {
                const result = { ...state.navigationGroupsCollapse };
                result[groupName] = newValue;
                state.navigationGroupsCollapse = result;
            });
            e.stopPropagation();
        },
        [noCollapse, show, groupName],
    );

    const routeList = useMemo(
        () => (
            <List component="div" disablePadding>
                {routes.map((route) => (
                    <RouteLink
                        key={route.path}
                        route={route}
                        hideNavigation={hideNavigation}
                        activeRoute={activeRoute}
                        highlightItem={highlightItem}
                    />
                ))}
            </List>
        ),
        [routes, hideNavigation, activeRoute, highlightItem],
    );

    return (
        <div>
            {groupName !== 'undefined' && (
                <ListItemButton
                    data-testid={`navigationGroup-${groupName.split('.')[1]}`}
                    key={groupName}
                    className={css.group}
                    onClick={handleButtonClick}
                >
                    <ListItemText primary={t.unknown(`${groupName}`)} />
                    {noCollapse ? <></> : show ? <ChevronRightIcon /> : <ExpandMore />}
                </ListItemButton>
            )}
            <Collapse in={!show || noCollapse} timeout="auto" unmountOnExit>
                {routeList}
            </Collapse>
        </div>
    );
});

export default function NavigationGroup({ routes, groupName, hideNavigation, disableCollapse, highlightItem }: NavigationGroupProps) {
    const navigationGroupsCollapse = appState.useState((s) => s.navigationGroupsCollapse) ?? {};
    const show = navigationGroupsCollapse[groupName];

    const url = useAppSelector((state) => state?.url);
    const activeRoute = useStableMemo(() => findActiveRoute(routes, url.path), [routes, url.path]);
    const hasActiveItem = !!activeRoute;
    const noCollapse = disableCollapse || hasActiveItem;

    return (
        <InnerNavigationGroup
            routes={useStableValue(routes)}
            groupName={groupName}
            hideNavigation={hideNavigation}
            highlightItem={highlightItem}
            activeRoute={activeRoute}
            noCollapse={noCollapse}
            show={show}
        />
    );
}

const InnerRouteLink = memo(function InnerRouteLink({
    route,
    hideNavigation,
    activeRoute,
    nested = 0,
    highlightItem,
    isOpen,
}: {
    route: RouteLike;
    hideNavigation: () => void;
    activeRoute?: RouteLike;
    nested?: number;
    highlightItem?: (item: RouteLike) => boolean | undefined;
    isOpen: boolean;
}) {
    const isActive = route.link === activeRoute?.link;
    const ref = useRef<HTMLAnchorElement>(null);
    const isHighlighted = highlightItem?.(route);
    const hasChildren = !!route.children?.length;

    const manuallyOpen = appState.useState((s) => s.navigationItemsOpen[route.path]) ?? false;
    const forcedOpen = isOpen;
    const showChildren = forcedOpen || manuallyOpen;

    const handleChevronClick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            appState.update((state) => {
                state.navigationItemsOpen = { ...state.navigationItemsOpen, [route.path]: !manuallyOpen };
            });
        },
        [manuallyOpen, route.path],
    );

    const pinnedIndex = appState.useState((s) => s.pinnedNavigationItems.indexOf(route.path));
    const pinnedCount = appState.useState((s) => s.pinnedNavigationItems.length);
    const isPinned = pinnedIndex !== -1;
    const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);

    const handleContextMenu = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setContextMenu({ mouseX: e.clientX, mouseY: e.clientY });
    }, []);

    const closeContextMenu = useCallback(() => setContextMenu(null), []);

    const togglePin = useCallback(() => {
        appState.update((state) => {
            state.pinnedNavigationItems = isPinned
                ? state.pinnedNavigationItems.filter((path) => path !== route.path)
                : [...state.pinnedNavigationItems, route.path];
        });
        setContextMenu(null);
    }, [isPinned, route.path]);

    const moveBy = useCallback(
        (offset: number) => {
            appState.update((state) => {
                const items = [...state.pinnedNavigationItems];
                const index = items.indexOf(route.path);
                const newIndex = index + offset;
                if (index === -1 || newIndex < 0 || newIndex >= items.length) return;
                const item = items[index]!;
                items[index] = items[newIndex]!;
                items[newIndex] = item;
                state.pinnedNavigationItems = items;
            });
            setContextMenu(null);
        },
        [route.path],
    );

    const moveUp = useCallback(() => moveBy(-1), [moveBy]);
    const moveDown = useCallback(() => moveBy(1), [moveBy]);

    const contextMenuElement = contextMenu && (
        <Menu
            open
            onClose={closeContextMenu}
            anchorReference="anchorPosition"
            anchorPosition={{ top: contextMenu.mouseY, left: contextMenu.mouseX }}
        >
            <MenuItem onClick={togglePin}>{isPinned ? t('navigation.unpin') : t('navigation.pin')}</MenuItem>
            {isPinned && (
                <MenuItem onClick={moveUp} disabled={pinnedIndex <= 0}>
                    {t('navigation.moveUp')}
                </MenuItem>
            )}
            {isPinned && (
                <MenuItem onClick={moveDown} disabled={pinnedIndex >= pinnedCount - 1}>
                    {t('navigation.moveDown')}
                </MenuItem>
            )}
        </Menu>
    );

    const isSearchResult = route.isMatchingSearch !== undefined;

    useEffect(() => {
        if (isPinned) return;
        if (ref.current) {
            if ((isActive && !isInViewport(ref.current)) || isHighlighted) {
                ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [isActive, isHighlighted, isPinned, isSearchResult]);

    function RouteIcon() {
        if (route.brandIcon) {
            return <BrandImage className={css.icon} imageKey={`icons.${route.brandIcon}`} size={24} />;
        }
        return <FontAwesomeIcon className={css.icon} fixedWidth icon={route.icon || faQuestion} />;
    }

    if (route.externalLink) {
        return (
            <>
                <ListItemButton
                    component="a"
                    href={route.externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={c(css.entry, { [css.highlighted]: isHighlighted })}
                    onContextMenu={handleContextMenu}
                    sx={{
                        pl: `${0.5 + Math.max(nested, 0)}em`,
                    }}
                >
                    {!nested ? <RouteIcon /> : <Box sx={subTreeMarkerSx} />}
                    <ListItemText primary={route.label} className={css.entryText} />
                </ListItemButton>
                {contextMenuElement}
            </>
        );
    }

    return (
        <>
            <ListItemButton
                data-testid={`navigationItem-${route.link.replace(/^\/+|\/+$/g, '').replace(/\/+/g, '-')}`}
                component={NavLink}
                forceActive={isActive}
                exact={route.exact}
                to={route?.link}
                className={c(css.entry, { [css.highlighted]: isHighlighted, [css.disableTransition]: !!highlightItem })}
                ref={ref}
                activeClassName={css.active}
                onClick={() => {
                    hideNavigation();
                    route?.onClick?.();
                }}
                onContextMenu={handleContextMenu}
                sx={{
                    pl: `${0.5 + Math.max(nested, 0)}em`,
                }}
            >
                {!nested ? <RouteIcon /> : <Box sx={subTreeMarkerSx} />}
                <ListItemText primary={route.label} className={css.entryText} />

                {hasChildren && (
                    <span
                        className={c(css.chevron, { [css.chevronDisabled]: forcedOpen })}
                        onClick={forcedOpen ? undefined : handleChevronClick}
                        aria-disabled={forcedOpen}
                    >
                        {showChildren ? <ExpandMore /> : <ChevronRightIcon />}
                    </span>
                )}
            </ListItemButton>

            {hasChildren && (
                <Collapse in={showChildren} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {route.children!.map((route) => (
                            <RouteLink
                                key={route.path}
                                route={route}
                                hideNavigation={hideNavigation}
                                activeRoute={activeRoute}
                                nested={nested + 1}
                                highlightItem={highlightItem}
                            />
                        ))}
                    </List>
                </Collapse>
            )}

            {contextMenuElement}
        </>
    );
});

function RouteLink({
    route,
    activeRoute,
    ...props
}: {
    route: RouteLike;
    hideNavigation: () => void;
    activeRoute?: RouteLike;
    nested?: number;
    highlightItem?: (item: RouteLike) => boolean | undefined;
}) {
    const path = useAppSelector((state) => state.url.path);
    const isOpen = route.isMatchingSearch || isLinkActive({ pathname: path, to: route.link });
    return <InnerRouteLink {...props} route={useStableValue(route)} activeRoute={useStableValue(activeRoute)} isOpen={isOpen} />;
}

function isInViewport(element: HTMLElement, offset = 0) {
    if (!element) return false;
    const top = element.getBoundingClientRect().top;
    return top + offset >= 0 && top - offset <= window.innerHeight;
}
